import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import LockIcon from "@mui/icons-material/Lock";
import RefreshIcon from "@mui/icons-material/Refresh";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import Swal from "sweetalert2";

import { domain } from "../auth/domain";
import { useAuth } from "../contexts/AuthContext";
import LocationSelector from "../components/LocationSelector";
import {
  createMigratedLoan,
  createMigration,
  fetchMigrationMembers,
  finalizeMigration,
  getMigration,
  importMigrationMembers,
  importMigrationMembersFile,
  listMigrations,
  saveBusinessBalance,
  saveOpeningBalances,
  saveSummaries,
  transitionMigration,
} from "../services/migrationService";
import { fetchGlobalSystemSettings } from "../services/systemSettingsService";
import { formatPhoneNumber } from "../shared/formatter";
import PhoneInput from "../components/PhoneInput";

const brand = "#800b33";

const STATUS_LABEL = {
  DRAFT: "Draft",
  IN_REVIEW: "In Review",
  APPROVED: "Approved",
  FINALIZED: "Finalized",
};

const STATUS_ORDER = ["DRAFT", "IN_REVIEW", "APPROVED", "FINALIZED"];
const STATUS_COLORS = {
  DRAFT: { bg: "#eeeeee", text: "#616161", border: "#bdbdbd" },
  IN_REVIEW: { bg: "#e3f2fd", text: "#1976d2", border: "#90caf9" },
  APPROVED: { bg: "#fff3e0", text: "#ef6c00", border: "#ffcc80" },
  FINALIZED: { bg: "#e8f5e9", text: "#2e7d32", border: "#a5d6a7" },
};

const WIZARD_STEPS = [
  { key: "group", label: "Group setup" },
  { key: "members", label: "Members" },
  { key: "loans", label: "Migrated loans" },
  { key: "balances", label: "Opening balances" },
  { key: "summaries", label: "Historical summaries" },
  { key: "business", label: "Business balances" },
  { key: "review", label: "Review & finalize" },
];

const ROLE_ALIASES = {
  chairperson: "chairperson",
  mwenyekiti: "chairperson",
  secretary: "secretary",
  katibu: "secretary",
  accountant: "accountant",
  muhasibu: "accountant",
};

const LEADERSHIP_LABELS = {
  chairperson: "Chairperson",
  secretary: "Secretary",
  accountant: "Accountant",
};

const getStatusChipStyles = (statusKey, filled) => {
  const palette = STATUS_COLORS[statusKey] || STATUS_COLORS.DRAFT;
  return {
    fontWeight: 700,
    borderRadius: 1,
    color: palette.text,
    borderColor: palette.border,
    backgroundColor: filled ? palette.bg : "transparent",
  };
};

function StatusPill({ status, label, active }) {
  return (
    <Chip
      label={label}
      variant={active ? "filled" : "outlined"}
      sx={getStatusChipStyles(status, active)}
    />
  );
}

export default function MigrationOnboarding() {
  const { user: authUser } = useAuth();

  const [useMock, setUseMock] = useState(!domain);
  const [activeStep, setActiveStep] = useState(0);
  const [busy, setBusy] = useState(false);

  const [confirm, setConfirm] = useState({ open: false, action: null });
  const [finalizeDialog, setFinalizeDialog] = useState({ open: false, typed: "" });
  const [transitionDialog, setTransitionDialog] = useState({
    open: false,
    title: "",
    nextStatus: "",
    reason: "",
  });

  const [migrations, setMigrations] = useState([]);
  const [loadMigrationId, setLoadMigrationId] = useState("");
  const [migration, setMigration] = useState(null);

  const [groupDraft, setGroupDraft] = useState({
    name: "Upendo SACCOS",
    mkoa: "Dar es Salaam",
    wilaya: "Kinondoni",
    start_date: "2018-01-01",
    end_date: "",
    cycle_type: "PERMANENT",
    frequency: "Weekly",
    contribution_amount: "10000",
    nmb_account_number: "",
    upatu_enabled: false,
    migration_date: "2024-12-31",
    notes: "",
  });

  const [members, setMembers] = useState([]);
  const [memberImportFile, setMemberImportFile] = useState(null);
  const nmbAccountValueRef = useRef("");
  const [manualMember, setManualMember] = useState({
    full_name: "",
    phone_number: "",
    role: "Member",
    join_date: "",
    number_of_slots: 1,
    email: "",
  });

  const [autoSeedZeros, setAutoSeedZeros] = useState(true);
  const [openingBalancesByMemberId, setOpeningBalancesByMemberId] = useState({});
  const [summariesByMemberId, setSummariesByMemberId] = useState({});
  const [businessBalance, setBusinessBalance] = useState({
    opening_cash_balance: "0",
    opening_assets_value: "0",
    notes: "",
  });

  const [loanDraft, setLoanDraft] = useState({
    member: "",
    outstanding_principal: "",
    outstanding_interest: "0",
    outstanding_df: "0",
    outstanding_arrears: "",
    term_months: "",
    term_type: "MONTHLY",
    interest_rate: "",
    df_percent: "",
  });
  const [migratedLoans, setMigratedLoans] = useState([]);

  const status = migration?.status || "DRAFT";
  const statusLabel = STATUS_LABEL[status] || status;
  const isLocked = status !== "DRAFT";
  const statusIndex = Math.max(0, STATUS_ORDER.indexOf(status));
  const isTemporaryGroup = groupDraft.cycle_type === "TEMPORARY";
  const groupStartDate = migration?.start_date || groupDraft.start_date;
  const normalizedAccountDigits = String(groupDraft.nmb_account_number || "").replace(/\D/g, "");
  const showAccountNumberError =
    !!groupDraft.nmb_account_number && normalizedAccountDigits.length !== 11;

  const parseAmount = (value) => {
    if (value === "" || value === null || value === undefined) return 0;
    const normalized = String(value).replace(/,/g, "").trim();
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : NaN;
  };

  const isNonNegative = (value) => {
    const parsed = parseAmount(value);
    return Number.isFinite(parsed) && parsed >= 0;
  };

  const formatMoney = (value) => new Intl.NumberFormat("en-US").format(Number(value) || 0);

  const showNotice = (icon, message) => {
    Swal.fire({
      icon,
      title: message,
      toast: true,
      position: "bottom-end",
      showConfirmButton: false,
      timer: 4500,
      timerProgressBar: true,
    });
  };
  const showError = (message) => showNotice("error", message);
  const showSuccess = (message) => showNotice("success", message);
  const showWarning = (message) => showNotice("warning", message);
  const showInfo = (message) => showNotice("info", message);

  const normalizeRole = (value) => ROLE_ALIASES[String(value || "").trim().toLowerCase()] || null;
  const normalizePhone = (value) => String(value || "").replace(/\D/g, "");
  const buildTemporaryPassword = (fullName, phoneNumber) => {
    const compactName = String(fullName || "").replace(/\s+/g, "");
    const prefix = (compactName || "user").slice(0, 3).toLowerCase();
    const last4 = normalizePhone(phoneNumber).slice(-4);
    return `${prefix}${last4}!`;
  };

  const leadershipStatus = useMemo(() => {
    const normalized = new Set();
    members.forEach((m) => {
      const normalizedRole = normalizeRole(m.role);
      if (normalizedRole) normalized.add(normalizedRole);
    });
    const required = ["chairperson", "secretary", "accountant"];
    const missing = required.filter((role) => !normalized.has(role));
    return { missing, normalized };
  }, [members]);

  const missingLeadershipLabels = leadershipStatus.missing.map((role) => LEADERSHIP_LABELS[role]);
  const chairpersonMember = useMemo(
    () => members.find((m) => normalizeRole(m.role) === "chairperson"),
    [members]
  );

  const resetAll = () => {
    setActiveStep(0);
    setMigration(null);
    setMembers([]);
    setMemberImportFile(null);
    setManualMember({
      full_name: "",
      phone_number: "",
      role: "Member",
      join_date: "",
      number_of_slots: 1,
      email: "",
    });
    setOpeningBalancesByMemberId({});
    setSummariesByMemberId({});
    setBusinessBalance({ opening_cash_balance: "0", opening_assets_value: "0", notes: "" });
    setLoanDraft({
      member: "",
      outstanding_principal: "",
      outstanding_interest: "0",
      outstanding_df: "0",
      outstanding_arrears: "",
      term_months: "",
      term_type: "MONTHLY",
      interest_rate: "",
      df_percent: "",
    });
    setMigratedLoans([]);
  };

  useEffect(() => {
    if (useMock) return;
    if (!domain) {
      setUseMock(true);
      return;
    }
    let mounted = true;
    (async () => {
      try {
        const data = await listMigrations();
        if (mounted) setMigrations(Array.isArray(data) ? data : []);
      } catch (error) {
        if (mounted) showWarning(error.message);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [useMock]);

  // Fetch global settings for SSC
  const [globalSettings, setGlobalSettings] = useState(null);
  useEffect(() => {
    if (useMock || !domain) return;
    fetchGlobalSystemSettings()
      .then((data) => setGlobalSettings(data))
      .catch((err) => console.error("Failed to load global settings:", err));
  }, [useMock]);

  // Auto-calculate SSC when principal changes
  useEffect(() => {
    if (!loanDraft.outstanding_principal) return;

    // Use the global SSC default
    const dfPercent = Number(globalSettings?.deposit_fee_percent || 10);

    // Auto-calculate using formula.
    const principal = parseAmount(loanDraft.outstanding_principal);
    const calculatedDf = (principal * (dfPercent / 100)).toFixed(0);

    setLoanDraft(prev => {
      const formattedDf = Number(calculatedDf).toLocaleString("en-US");
      // Avoid infinite loop if value is same
      if (String(prev.outstanding_df) === formattedDf) return prev;
      return { ...prev, outstanding_df: formattedDf };
    });
  }, [loanDraft.outstanding_principal, globalSettings]);

  useEffect(() => {
    if (!autoSeedZeros) return;
    if (!members.length) return;

    setOpeningBalancesByMemberId((prev) => {
      const next = { ...prev };
      members.forEach((m) => {
        if (!next[m.id]) {
          next[m.id] = {
            member: m.id,
            opening_savings_balance: "0",
            opening_loan_balance: "0",
            opening_arrears_balance: "0",
            opening_special_contribution_balance: "0",
            opening_emergency_contribution_balance: "0",
            notes: "",
          };
        }
      });
      return next;
    });

    setSummariesByMemberId((prev) => {
      const next = { ...prev };
      members.forEach((m) => {
        if (!next[m.id]) {
          next[m.id] = {
            member: m.id,
            total_contributions: "0",
            total_loans: "0",
            total_penalties: "0",
            notes: "",
          };
        }
      });
      return next;
    });
  }, [autoSeedZeros, members]);

  const downloadMembersCsvTemplate = () => {
    const headers = [
      "full_name",
      "phone_number",
      "role",
      "join_date",
      "number_of_slots",
      "email",
      "nmb_account_number",
      "identity_type",
      "identity_number",
    ];
    const sample = [
      "Asha Mrema,0712445111,Chairperson,2018-02-14,1,asha@example.com,,,",
      "Juma Komba,0712445778,Secretary,2018-02-14,1,juma@example.com,,,",
    ];
    const content = [headers.join(","), ...sample].join("\n");
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "migration_members_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleCreateGroup = async () => {
    const accountDigits = String(nmbAccountValueRef.current || groupDraft.nmb_account_number || "")
      .replace(/\D/g, "")
      .slice(0, 11);

    const required = [
      "name",
      "mkoa",
      "wilaya",
      "start_date",
      "migration_date",
      "cycle_type",
      "frequency",
      "contribution_amount",
    ];
    const missing = required.filter((k) => !String(groupDraft[k] ?? "").trim());
    if (missing.length) {
      showError(`Missing required fields: ${missing.join(", ")}`);
      return;
    }
    if (!isNonNegative(groupDraft.contribution_amount)) {
      showError("Contribution amount must be a non-negative number.");
      return;
    }
    if (isTemporaryGroup && !String(groupDraft.end_date || "").trim()) {
      showError("End date is required for temporary groups.");
      return;
    }
    if (groupDraft.end_date && groupDraft.start_date && groupDraft.end_date < groupDraft.start_date) {
      showError("End date must be after start date.");
      return;
    }
    if (groupDraft.migration_date && groupDraft.start_date && groupDraft.migration_date < groupDraft.start_date) {
      showError("Migration date must be on/after group start date.");
      return;
    }
    if (accountDigits && accountDigits.length !== 11) {
      showError("NMB account must be exactly 11 digits.");
      return;
    }

    if (useMock) {
      setMigration({
        id: 1,
        group_id: 1,
        group_name: groupDraft.name,
        mkoa: groupDraft.mkoa,
        wilaya: groupDraft.wilaya,
        start_date: groupDraft.start_date,
        end_date: groupDraft.end_date || null,
        cycle_type: groupDraft.cycle_type,
        is_migrated: true,
        status: "DRAFT",
        migration_date: groupDraft.migration_date,
      });
      setActiveStep(1);
      showSuccess("Draft created (mock).");
      return;
    }

    setBusy(true);
    try {
      const payload = {
        ...groupDraft,
        contribution_amount: String(parseAmount(groupDraft.contribution_amount)),
        end_date: groupDraft.end_date ? groupDraft.end_date : null,
        nmb_account_number: accountDigits || "",
      };
      const mg = await createMigration(payload);
      setMigration(mg);
      setMembers([]);
      setActiveStep(1);
      showSuccess(`Migration draft created (#${mg.id}).`);
    } catch (error) {
      showError(error.message);
    } finally {
      setBusy(false);
    }
  };

  const handleLoadMigration = async (overrideId) => {
    const migrationId = String(overrideId ?? loadMigrationId).trim();
    if (!migrationId) return;
    if (useMock) {
      showError("Switch off Mock Mode to load a real migration.");
      return;
    }
    setBusy(true);
    try {
      const mg = await getMigration(migrationId);
      setMigration(mg);
      const mm = await fetchMigrationMembers(mg.id);
      setMembers(Array.isArray(mm) ? mm : []);
      setActiveStep(1);
      showSuccess(`Loaded migration #${mg.id}.`);
    } catch (error) {
      showError(error.message);
    } finally {
      setBusy(false);
    }
  };

  const normalizedPhone = formatPhoneNumber(manualMember.phone_number);
  const phoneTaken = normalizedPhone && members.some((m) => m.phone_number === normalizedPhone);
  const joinDateTooEarly =
    manualMember.join_date && groupStartDate && manualMember.join_date < groupStartDate;
  const canAddMember =
    manualMember.full_name.trim() &&
    normalizedPhone &&
    manualMember.join_date &&
    !phoneTaken &&
    Number(manualMember.number_of_slots) >= 1 &&
    !joinDateTooEarly;

  const handleAddMember = async () => {
    if (!canAddMember) return;
    if (joinDateTooEarly) {
      showError("Join date cannot be earlier than the group start date.");
      return;
    }
    const payload = {
      full_name: manualMember.full_name.trim(),
      phone_number: formatPhoneNumber(normalizedPhone),
      role: manualMember.role,
      join_date: manualMember.join_date,
      number_of_slots: Number(manualMember.number_of_slots) || 1,
      email: manualMember.email?.trim() || "",
    };

    if (useMock) {
      setMembers((prev) => [
        ...prev,
        {
          id: Date.now(),
          full_name: payload.full_name,
          phone_number: payload.phone_number,
          role: payload.role,
          join_date: payload.join_date,
          number_of_slots: payload.number_of_slots,
          is_migrated: true,
        },
      ]);
      setManualMember({
        full_name: "",
        phone_number: "",
        role: "Member",
        join_date: "",
        number_of_slots: 1,
        email: "",
      });
      showSuccess("Member added (mock).");
      return;
    }

    if (!migration?.id) {
      showError("Create a migration draft first.");
      return;
    }
    setBusy(true);
    try {
      await importMigrationMembers(migration.id, [payload]);
      const mm = await fetchMigrationMembers(migration.id);
      setMembers(Array.isArray(mm) ? mm : []);
      setManualMember({
        full_name: "",
        phone_number: "",
        role: "Member",
        join_date: "",
        number_of_slots: 1,
        email: "",
      });
      showSuccess("Member saved.");
    } catch (error) {
      showError(error.message);
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteMember = (memberId) => {
    if (isLocked) return;
    if (!useMock) {
      showError("Deleting members is not supported yet. Use backend tools if needed.");
      return;
    }
    setMembers((prev) => prev.filter((m) => m.id !== memberId));
  };

  const handleUploadMembers = async () => {
    if (!memberImportFile) return;
    if (useMock) {
      showError("File import is disabled in Mock Mode.");
      return;
    }
    if (!migration?.id) {
      showError("Create a migration draft first.");
      return;
    }
    setBusy(true);
    try {
      await importMigrationMembersFile(migration.id, memberImportFile);
      const mm = await fetchMigrationMembers(migration.id);
      setMembers(Array.isArray(mm) ? mm : []);
      setMemberImportFile(null);
      showSuccess("Members imported.");
    } catch (error) {
      showError(error.message);
    } finally {
      setBusy(false);
    }
  };

  const setOpeningBalanceField = (memberId, field) => (event) => {
    const value = event.target.value;
    setOpeningBalancesByMemberId((prev) => ({
      ...prev,
      [memberId]: { ...(prev[memberId] || { member: memberId }), [field]: value },
    }));
  };

  const setSummaryField = (memberId, field) => (event) => {
    const value = event.target.value;
    setSummariesByMemberId((prev) => ({
      ...prev,
      [memberId]: { ...(prev[memberId] || { member: memberId }), [field]: value },
    }));
  };

  const canSaveOpeningBalances = useMemo(() => {
    if (!members.length) return false;
    return members.every((m) => {
      const r = openingBalancesByMemberId[m.id];
      if (!r) return false;
      return [
        r.opening_savings_balance,
        r.opening_arrears_balance,
        r.opening_special_contribution_balance,
        r.opening_emergency_contribution_balance,
      ].every(isNonNegative);
    });
  }, [members, openingBalancesByMemberId]);

  const handleSaveOpeningBalances = async () => {
    if (!members.length) return;
    if (!canSaveOpeningBalances) {
      showError("Fill opening balances for every member (0 is allowed).");
      return;
    }

    const balances = members.map((m) => {
      const r = openingBalancesByMemberId[m.id];
      const computedLoanTotal = migratedLoans
        .filter((l) => Number(l.member) === Number(m.id))
        .reduce(
          (sum, l) =>
            sum +
            parseAmount(l.outstanding_principal) +
            parseAmount(l.outstanding_interest) +
            parseAmount(l.outstanding_df) +
            parseAmount(l.outstanding_arrears || 0),
          0
        );

      return {
        member: m.id,
        opening_savings_balance: String(parseAmount(r.opening_savings_balance)),
        opening_loan_balance: String(Math.max(0, computedLoanTotal)),
        opening_arrears_balance: String(parseAmount(r.opening_arrears_balance)),
        opening_special_contribution_balance: String(parseAmount(r.opening_special_contribution_balance)),
        opening_emergency_contribution_balance: String(parseAmount(r.opening_emergency_contribution_balance)),
        notes: r.notes || "",
        source: "MANUAL",
      };
    });

    if (useMock) {
      showSuccess("Opening balances saved (mock).");
      return;
    }
    if (!migration?.id) {
      showError("Create a migration draft first.");
      return;
    }
    setBusy(true);
    try {
      const result = await saveOpeningBalances(migration.id, balances);
      const errors = Array.isArray(result?.errors) ? result.errors : [];
      if (errors.length) {
        const createdCount = Array.isArray(result?.created) ? result.created.length : 0;
        const updatedCount = Array.isArray(result?.updated) ? result.updated.length : 0;
        showWarning(
          `Opening balances saved with ${errors.length} error(s). Applied ${createdCount + updatedCount}.`
        );
      } else {
        showSuccess("Opening balances saved.");
      }
    } catch (error) {
      showError(error.message);
    } finally {
      setBusy(false);
    }
  };

  const canSaveSummaries = useMemo(() => {
    if (!members.length) return false;
    return members.every((m) => {
      const r = summariesByMemberId[m.id];
      if (!r) return false;
      return [r.total_contributions, r.total_loans, r.total_penalties].every(isNonNegative);
    });
  }, [members, summariesByMemberId]);

  const handleSaveSummaries = async () => {
    if (!members.length) return;
    if (!canSaveSummaries) {
      showError("Fill summaries for every member (0 is allowed).");
      return;
    }
    const summaries = members.map((m) => {
      const r = summariesByMemberId[m.id];
      return {
        member: m.id,
        total_contributions: String(parseAmount(r.total_contributions)),
        total_loans: String(parseAmount(r.total_loans)),
        total_penalties: String(parseAmount(r.total_penalties)),
        notes: r.notes || "",
        source: "MANUAL",
      };
    });

    if (useMock) {
      showSuccess("Summaries saved (mock).");
      return;
    }
    if (!migration?.id) {
      showError("Create a migration draft first.");
      return;
    }
    setBusy(true);
    try {
      const result = await saveSummaries(migration.id, summaries);
      const errors = Array.isArray(result?.errors) ? result.errors : [];
      if (errors.length) {
        const createdCount = Array.isArray(result?.created) ? result.created.length : 0;
        const updatedCount = Array.isArray(result?.updated) ? result.updated.length : 0;
        showWarning(`Summaries saved with ${errors.length} error(s). Applied ${createdCount + updatedCount}.`);
      } else {
        showSuccess("Historical summaries saved.");
      }
    } catch (error) {
      showError(error.message);
    } finally {
      setBusy(false);
    }
  };

  const canSaveLoanDraft = useMemo(() => {
    if (!loanDraft.member) return false;
    if (!isNonNegative(loanDraft.outstanding_principal) || parseAmount(loanDraft.outstanding_principal) <= 0) {
      return false;
    }
    if (!isNonNegative(loanDraft.outstanding_interest)) return false;
    if (!isNonNegative(loanDraft.outstanding_df)) return false;
    if (loanDraft.outstanding_arrears !== "" && !isNonNegative(loanDraft.outstanding_arrears)) return false;
    if (!Number(loanDraft.term_months) || Number(loanDraft.term_months) <= 0) return false;
    return true;
  }, [loanDraft]);

  const handleAddMigratedLoan = async () => {
    if (!canSaveLoanDraft) return;
    const payload = {
      member: Number(loanDraft.member),
      outstanding_principal: String(parseAmount(loanDraft.outstanding_principal)),
      outstanding_interest: String(parseAmount(loanDraft.outstanding_interest)),
      outstanding_df: String(parseAmount(loanDraft.outstanding_df)),
      outstanding_arrears:
        loanDraft.outstanding_arrears === "" ? null : String(parseAmount(loanDraft.outstanding_arrears)),
      term_months: Number(loanDraft.term_months),
      term_type: loanDraft.term_type,
    };
    if (loanDraft.interest_rate !== "") payload.interest_rate = String(parseAmount(loanDraft.interest_rate));
    if (loanDraft.df_percent !== "") payload.df_percent = String(parseAmount(loanDraft.df_percent));

    if (useMock) {
      setMigratedLoans((prev) => [...prev, payload]);
      setLoanDraft({
        member: "",
        outstanding_principal: "",
        outstanding_interest: "0",
        outstanding_df: "0",
        outstanding_arrears: "",
        term_months: "",
        term_type: "MONTHLY",
        interest_rate: "",
        df_percent: "",
      });
      showSuccess("Migrated loan added (mock).");
      return;
    }

    if (!migration?.id) {
      showError("Create a migration draft first.");
      return;
    }
    setBusy(true);
    try {
      const created = await createMigratedLoan(migration.id, payload);
      setMigratedLoans((prev) => [...prev, { ...payload, _server: created }]);
      setLoanDraft({
        member: "",
        outstanding_principal: "",
        outstanding_interest: "0",
        outstanding_df: "0",
        outstanding_arrears: "",
        term_months: "",
        term_type: "MONTHLY",
        interest_rate: "",
        df_percent: "",
      });
      showSuccess("Migrated loan created.");
    } catch (error) {
      showError(error.message);
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveLoan = (index) => {
    if (isLocked) return;
    setMigratedLoans((prev) => prev.filter((_, idx) => idx !== index));
  };

  const canSaveBusinessBalance = useMemo(() => {
    return isNonNegative(businessBalance.opening_cash_balance) && isNonNegative(businessBalance.opening_assets_value);
  }, [businessBalance]);

  const handleSaveBusiness = async () => {
    if (!canSaveBusinessBalance) {
      showError("Business balances must be non-negative numbers.");
      return;
    }
    const payload = {
      opening_cash_balance: String(parseAmount(businessBalance.opening_cash_balance)),
      opening_assets_value: String(parseAmount(businessBalance.opening_assets_value)),
      source: "MANUAL",
      notes: businessBalance.notes || "",
    };
    if (useMock) {
      showSuccess("Business balances saved (mock).");
      return;
    }
    if (!migration?.id) {
      showError("Create a migration draft first.");
      return;
    }
    setBusy(true);
    try {
      await saveBusinessBalance(migration.id, payload);
      showSuccess("Business balances saved.");
    } catch (error) {
      showError(error.message);
    } finally {
      setBusy(false);
    }
  };

  const handleTransition = async (nextStatus, reason) => {
    if (useMock) {
      setMigration((prev) => ({ ...(prev || {}), status: nextStatus }));
      showSuccess(`Status updated to ${STATUS_LABEL[nextStatus] || nextStatus} (mock).`);
      return;
    }
    if (!migration?.id) return;
    setBusy(true);
    try {
      const updated = await transitionMigration(migration.id, nextStatus, reason);
      setMigration(updated);
      showSuccess(`Status updated to ${STATUS_LABEL[updated.status] || updated.status}.`);
    } catch (error) {
      showError(error.message);
    } finally {
      setBusy(false);
    }
  };

  const handleSubmitForReview = () => {
    if (!canSubmitForReview) {
      showError(reviewBlockers[0] || "Resolve review checklist items before submitting.");
      return;
    }
    handleTransition("IN_REVIEW");
  };

  const handleApprove = () => {
    if (!canApprove) {
      showError(reviewBlockers[0] || "Resolve review checklist items before approving.");
      return;
    }
    handleTransition("APPROVED");
  };

  const handleConfirmTransition = () => {
    const reason = transitionDialog.reason.trim();
    if (!reason) {
      showError("Reason is required to return a migration to Draft.");
      return;
    }
    const nextStatus = transitionDialog.nextStatus;
    setTransitionDialog({ open: false, title: "", nextStatus: "", reason: "" });
    handleTransition(nextStatus, reason);
  };

  const handleFinalize = async () => {
    if (status !== "APPROVED") {
      showError("Migration must be Approved before Finalization.");
      return;
    }
    if (reviewBlockers.length) {
      showError(reviewBlockers[0]);
      return;
    }
    const expected = (migration?.group_name || groupDraft.name || "").trim();
    if (finalizeDialog.typed.trim() !== expected) {
      showError("Typed group name does not match.");
      return;
    }

    if (useMock) {
      setMigration((prev) => ({ ...(prev || {}), status: "FINALIZED" }));
      setFinalizeDialog({ open: false, typed: "" });
      if (chairpersonMember) {
        const tempPassword = buildTemporaryPassword(chairpersonMember.full_name, chairpersonMember.phone_number);
        showSuccess(
          `Migration finalized (mock). Chairperson login: ${chairpersonMember.phone_number} / ${tempPassword}.`
        );
      } else {
        showSuccess("Migration finalized (mock).");
      }
      return;
    }

    if (!migration?.id) return;
    setBusy(true);
    try {
      const result = await finalizeMigration(migration.id);
      const updated = result?.migration || result;
      setMigration(updated);
      setFinalizeDialog({ open: false, typed: "" });
      const credentials = result?.chairperson_credentials;
      if (credentials?.status === "created") {
        showSuccess(
          `Migration finalized. Chairperson login: ${credentials.username} / ${credentials.temporary_password}.`
        );
      } else if (credentials?.status === "existing") {
        showWarning(`Migration finalized. ${credentials.message || "Chairperson credentials already active."}`);
      } else {
        showSuccess("Migration finalized.");
      }
    } catch (error) {
      showError(error.message);
    } finally {
      setBusy(false);
    }
  };

  const stepTitle = WIZARD_STEPS[activeStep]?.label || "Migration";
  const groupNameDisplay = migration?.group_name || groupDraft.name || "Migration";
  const progress = ((activeStep + 1) / WIZARD_STEPS.length) * 100;

  const canGoNext = useMemo(() => {
    if (activeStep === 0) return useMock ? Boolean(groupDraft.name && groupDraft.migration_date) : Boolean(migration?.id);
    if (!migration && !useMock) return false;
    if (activeStep === 1) return members.length > 0;
    return true;
  }, [activeStep, groupDraft, members.length, migration?.id, migration, useMock]);

  const totals = useMemo(() => {
    const totalSavings = members.reduce(
      (sum, m) => sum + parseAmount(openingBalancesByMemberId[m.id]?.opening_savings_balance || 0),
      0
    );
    const totalSpecial = members.reduce(
      (sum, m) => sum + parseAmount(openingBalancesByMemberId[m.id]?.opening_special_contribution_balance || 0),
      0
    );
    const totalEmergency = members.reduce(
      (sum, m) => sum + parseAmount(openingBalancesByMemberId[m.id]?.opening_emergency_contribution_balance || 0),
      0
    );
    const totalArrears = members.reduce(
      (sum, m) => sum + parseAmount(openingBalancesByMemberId[m.id]?.opening_arrears_balance || 0),
      0
    );
    const totalLoanOutstanding = migratedLoans.reduce(
      (sum, l) =>
        sum +
        parseAmount(l.outstanding_principal) +
        parseAmount(l.outstanding_interest) +
        parseAmount(l.outstanding_df) +
        parseAmount(l.outstanding_arrears || 0),
      0
    );
    return { totalSavings, totalSpecial, totalEmergency, totalArrears, totalLoanOutstanding };
  }, [members, openingBalancesByMemberId, migratedLoans]);

  const missingBalances = useMemo(
    () => members.filter((m) => !openingBalancesByMemberId[m.id]).length,
    [members, openingBalancesByMemberId]
  );
  const missingSummaries = useMemo(
    () => members.filter((m) => !summariesByMemberId[m.id]).length,
    [members, summariesByMemberId]
  );
  const reviewBlockers = useMemo(() => {
    const issues = [];
    if (members.length === 0) {
      issues.push("Add at least one member.");
    }
    if (missingBalances > 0 || missingSummaries > 0) {
      issues.push("Complete opening balances and summaries for all members.");
    }
    if (missingLeadershipLabels.length > 0) {
      issues.push(`Missing leadership roles: ${missingLeadershipLabels.join(", ")}.`);
    }
    return issues;
  }, [members.length, missingBalances, missingSummaries, missingLeadershipLabels]);
  const canSubmitForReview = status === "DRAFT" && reviewBlockers.length === 0;
  const canApprove = status === "IN_REVIEW" && reviewBlockers.length === 0;
  const canFinalize = status === "APPROVED" && reviewBlockers.length === 0;

  const renderStep = () => {
    if (activeStep === 0) {
      return (
        <Box>
          <Stack spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: brand }}>
              Group Migration Setup
            </Typography>
            <Alert severity="info">
              Migration captures <b>opening balances only</b>. The system starts normal operations{" "}
              <b>from the migration date forward</b> after finalization.
            </Alert>

            {!useMock && (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  alignItems={{ md: "center" }}
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography sx={{ fontWeight: 800 }}>Resume a migration</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Load by ID or open a recent migration.
                    </Typography>
                  </Box>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center">
                    <TextField
                      label="Migration ID"
                      value={loadMigrationId}
                      onChange={(e) => setLoadMigrationId(e.target.value)}
                      size="small"
                      sx={{ minWidth: 180 }}
                    />
                    <Button
                      variant="outlined"
                      onClick={() => handleLoadMigration()}
                      disabled={busy || !loadMigrationId.trim()}
                    >
                      Load
                    </Button>
                  </Stack>
                </Stack>

                {migrations.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                      Recent migrations
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Group</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Migration date</TableCell>
                          <TableCell />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {migrations.slice(0, 5).map((m) => (
                          <TableRow key={m.id}>
                            <TableCell>{m.id}</TableCell>
                            <TableCell>{m.group_name}</TableCell>
                            <TableCell>
                              <Chip
                                label={STATUS_LABEL[m.status] || m.status}
                                variant="outlined"
                                sx={getStatusChipStyles(m.status, true)}
                              />
                            </TableCell>
                            <TableCell>{m.migration_date}</TableCell>
                            <TableCell align="right">
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => {
                                  setLoadMigrationId(String(m.id));
                                  handleLoadMigration(m.id);
                                }}
                              >
                                Open
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </Paper>
            )}

            <Divider />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Group name"
                  value={groupDraft.name}
                  onChange={(e) => setGroupDraft((p) => ({ ...p, name: e.target.value }))}
                  fullWidth
                  disabled={!!migration?.id && !useMock}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <LocationSelector
                  region={groupDraft.mkoa}
                  district={groupDraft.wilaya}
                  disabled={!!migration?.id && !useMock}
                  onChange={({ region, district }) =>
                    setGroupDraft((p) => ({ ...p, mkoa: region, wilaya: district }))
                  }
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Start date"
                  type="date"
                  value={groupDraft.start_date}
                  onChange={(e) => setGroupDraft((p) => ({ ...p, start_date: e.target.value }))}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  disabled={!!migration?.id && !useMock}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label={isTemporaryGroup ? "End date" : "End date (optional)"}
                  type="date"
                  value={groupDraft.end_date}
                  onChange={(e) => setGroupDraft((p) => ({ ...p, end_date: e.target.value }))}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  disabled={!!migration?.id && !useMock}
                  required={isTemporaryGroup}
                  error={Boolean(
                    (isTemporaryGroup && !groupDraft.end_date) ||
                    (groupDraft.end_date && groupDraft.start_date && groupDraft.end_date < groupDraft.start_date)
                  )}
                  helperText={
                    isTemporaryGroup && !groupDraft.end_date
                      ? "Required for temporary groups."
                      : groupDraft.end_date && groupDraft.start_date && groupDraft.end_date < groupDraft.start_date
                        ? "End date must be after start date."
                        : ""
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Migration date (snapshot)"
                  type="date"
                  value={groupDraft.migration_date}
                  onChange={(e) => setGroupDraft((p) => ({ ...p, migration_date: e.target.value }))}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  disabled={!!migration?.id && !useMock}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  select
                  label="Cycle type"
                  value={groupDraft.cycle_type}
                  onChange={(e) => setGroupDraft((p) => ({ ...p, cycle_type: e.target.value }))}
                  fullWidth
                  disabled={!!migration?.id && !useMock}
                >
                  <MenuItem value="TEMPORARY">Temporary</MenuItem>
                  <MenuItem value="PERMANENT">Permanent</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  label="Meeting frequency"
                  value={groupDraft.frequency}
                  onChange={(e) => setGroupDraft((p) => ({ ...p, frequency: e.target.value }))}
                  fullWidth
                  disabled={!!migration?.id && !useMock}
                >
                  <MenuItem value="Weekly">Weekly</MenuItem>
                  <MenuItem value="Monthly">Monthly</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Contribution amount (TZS)"
                  value={groupDraft.contribution_amount}
                  onChange={(e) => setGroupDraft((p) => ({ ...p, contribution_amount: e.target.value }))}
                  fullWidth
                  disabled={!!migration?.id && !useMock}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="NMB account number (optional)"
                  value={groupDraft.nmb_account_number}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, "").slice(0, 11);
                    nmbAccountValueRef.current = digits;
                    setGroupDraft((p) => ({ ...p, nmb_account_number: digits }));
                  }}
                  fullWidth
                  disabled={!!migration?.id && !useMock}
                  inputProps={{ inputMode: "numeric", pattern: "[0-9]*", maxLength: 11 }}
                  error={showAccountNumberError}
                  helperText={
                    showAccountNumberError
                      ? "NMB account must be exactly 11 digits."
                      : "Optional: 11-digit NMB business account."
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Notes (optional)"
                  value={groupDraft.notes}
                  onChange={(e) => setGroupDraft((p) => ({ ...p, notes: e.target.value }))}
                  fullWidth
                  disabled={!!migration?.id && !useMock}
                />
              </Grid>
            </Grid>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={handleCreateGroup}
                disabled={busy || (!!migration?.id && !useMock)}
              >
                Create draft
              </Button>
            </Stack>
          </Stack>
        </Box>
      );
    }

    if (activeStep === 1) {
      return (
        <Box>
          <Stack spacing={2}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ md: "center" }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: brand }}>
                  Members (Migrated)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Members can be created without login credentials initially. Join date is captured for audit.
                </Typography>
              </Box>
              {/* <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadMembersCsvTemplate}>
                  CSV template
                </Button>
                <Button variant="outlined" component="label" startIcon={<UploadFileIcon />} disabled={busy || isLocked}>
                  Choose file
                  <input
                    hidden
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={(e) => setMemberImportFile(e.target.files?.[0] || null)}
                  />
                </Button>
                <Button
                  variant="contained"
                  onClick={handleUploadMembers}
                  disabled={busy || !memberImportFile || isLocked || useMock}
                >
                  Upload
                </Button>
              </Stack> */}
            </Stack>

            {/* <Alert severity="info">
              Excel support depends on the backend having <b>openpyxl</b>. If Excel fails, upload CSV instead.
            </Alert> */}

            <Paper sx={{ p: 2, borderRadius: 3, background: "#f5f5f5" }}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Manual member entry
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Full name"
                      value={manualMember.full_name}
                      onChange={(e) => setManualMember((p) => ({ ...p, full_name: e.target.value }))}
                      fullWidth
                      disabled={busy || isLocked}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <PhoneInput
                      label="Phone number"
                      value={manualMember.phone_number}
                      onChange={(val) => setManualMember((p) => ({ ...p, phone_number: val }))}
                      error={phoneTaken}
                      helperText={phoneTaken ? "Phone already exists in this group." : ""}
                      fullWidth
                      disabled={busy || isLocked}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      select
                      label="Role"
                      value={manualMember.role}
                      onChange={(e) => setManualMember((p) => ({ ...p, role: e.target.value }))}
                      fullWidth
                      disabled={busy || isLocked}
                    >
                      <MenuItem value="Chairperson">Chairperson</MenuItem>
                      <MenuItem value="Secretary">Secretary</MenuItem>
                      <MenuItem value="Accountant">Accountant</MenuItem>
                      <MenuItem value="Member">Member</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      label="Join date"
                      type="date"
                      value={manualMember.join_date}
                      onChange={(e) => setManualMember((p) => ({ ...p, join_date: e.target.value }))}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      disabled={busy || isLocked}
                      error={joinDateTooEarly}
                      helperText={
                        joinDateTooEarly ? "Join date cannot be earlier than group start date." : ""
                      }
                      inputProps={groupStartDate ? { min: groupStartDate } : undefined}
                    />
                  </Grid>
                  <Grid item xs={12} md={1}>
                    <TextField
                      label="Slots"
                      type="number"
                      value={manualMember.number_of_slots}
                      onChange={(e) => setManualMember((p) => ({ ...p, number_of_slots: e.target.value }))}
                      inputProps={{ min: 1, step: 1 }}
                      fullWidth
                      disabled={busy || isLocked}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Email (optional)"
                      value={manualMember.email}
                      onChange={(e) => setManualMember((p) => ({ ...p, email: e.target.value }))}
                      fullWidth
                      disabled={busy || isLocked}
                    />
                  </Grid>
                </Grid>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="flex-end">
                  <Button variant="contained" onClick={handleAddMember} disabled={!canAddMember || busy || isLocked}>
                    Add member
                  </Button>
                </Stack>
              </Stack>
            </Paper>

            <Divider />

            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ md: "center" }}
              justifyContent="space-between"
            >
              <Typography sx={{ fontWeight: 800 }}>Current members ({members.length})</Typography>
              <FormControlLabel
                control={<Switch checked={autoSeedZeros} onChange={(e) => setAutoSeedZeros(e.target.checked)} />}
                label="Auto seed zero records (balances & summaries)"
              />
            </Stack>

            {members.length === 0 ? (
              <Alert severity="warning">Add at least one member to continue.</Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Member</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Join date</TableCell>
                    <TableCell>Slots</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {members.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell>{m.full_name}</TableCell>
                      <TableCell>{m.phone_number}</TableCell>
                      <TableCell>{m.role}</TableCell>
                      <TableCell>{m.join_date}</TableCell>
                      <TableCell>{m.number_of_slots}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" color="error" disabled={isLocked} onClick={() => handleDeleteMember(m.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Stack>
        </Box>
      );
    }

    if (activeStep === 3) {
      return (
        <Box>
          <Stack spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: brand }}>
              Opening Balances (Opening Balance doctrine)
            </Typography>
            <Alert severity="info">
              These values are <b>read-only historical snapshots</b>. No penalties are calculated on opening balances.
            </Alert>

            {members.length === 0 ? (
              <Alert severity="warning">Add members first.</Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Member</TableCell>
                    <TableCell align="right">Savings (TZS)</TableCell>
                    <TableCell align="right">Special (TZS)</TableCell>
                    <TableCell align="right">Emergency (TZS)</TableCell>
                    <TableCell align="right">Arrears (TZS)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {members.map((m) => {
                    const r = openingBalancesByMemberId[m.id] || {};
                    return (
                      <TableRow key={m.id}>
                        <TableCell>
                          <Typography sx={{ fontWeight: 700 }}>{m.full_name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {m.phone_number}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            value={r.opening_savings_balance ?? ""}
                            onChange={setOpeningBalanceField(m.id, "opening_savings_balance")}
                            size="small"
                            disabled={isLocked}
                            error={r.opening_savings_balance !== undefined && !isNonNegative(r.opening_savings_balance)}
                            sx={{ width: 140 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            value={r.opening_special_contribution_balance ?? ""}
                            onChange={setOpeningBalanceField(m.id, "opening_special_contribution_balance")}
                            size="small"
                            disabled={isLocked}
                            error={
                              r.opening_special_contribution_balance !== undefined &&
                              !isNonNegative(r.opening_special_contribution_balance)
                            }
                            sx={{ width: 140 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            value={r.opening_emergency_contribution_balance ?? ""}
                            onChange={setOpeningBalanceField(m.id, "opening_emergency_contribution_balance")}
                            size="small"
                            disabled={isLocked}
                            error={
                              r.opening_emergency_contribution_balance !== undefined &&
                              !isNonNegative(r.opening_emergency_contribution_balance)
                            }
                            sx={{ width: 140 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            value={r.opening_arrears_balance ?? ""}
                            onChange={setOpeningBalanceField(m.id, "opening_arrears_balance")}
                            size="small"
                            disabled={isLocked}
                            error={r.opening_arrears_balance !== undefined && !isNonNegative(r.opening_arrears_balance)}
                            sx={{ width: 140 }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={handleSaveOpeningBalances}
                disabled={busy || isLocked || members.length === 0 || !canSaveOpeningBalances}
              >
                Save opening balances
              </Button>
            </Stack>
          </Stack>
        </Box>
      );
    }

    if (activeStep === 4) {
      return (
        <Box>
          <Stack spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: brand }}>
              Historical Summaries (Optional)
            </Typography>
            <Alert severity="info">
              Summaries are totals only (no day-by-day logs). They are excluded from penalty logic and become read-only
              after save.
            </Alert>

            {members.length === 0 ? (
              <Alert severity="warning">Add members first.</Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Member</TableCell>
                    <TableCell align="right">Total contributions</TableCell>
                    <TableCell align="right">Total loans</TableCell>
                    <TableCell align="right">Total penalties</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {members.map((m) => {
                    const r = summariesByMemberId[m.id] || {};
                    return (
                      <TableRow key={m.id}>
                        <TableCell>
                          <Typography sx={{ fontWeight: 700 }}>{m.full_name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {m.phone_number}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            value={r.total_contributions ?? ""}
                            onChange={setSummaryField(m.id, "total_contributions")}
                            size="small"
                            disabled={isLocked}
                            error={r.total_contributions !== undefined && !isNonNegative(r.total_contributions)}
                            sx={{ width: 160 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            value={r.total_loans ?? ""}
                            onChange={setSummaryField(m.id, "total_loans")}
                            size="small"
                            disabled={isLocked}
                            error={r.total_loans !== undefined && !isNonNegative(r.total_loans)}
                            sx={{ width: 160 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            value={r.total_penalties ?? ""}
                            onChange={setSummaryField(m.id, "total_penalties")}
                            size="small"
                            disabled={isLocked}
                            error={r.total_penalties !== undefined && !isNonNegative(r.total_penalties)}
                            sx={{ width: 160 }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={handleSaveSummaries}
                disabled={busy || isLocked || members.length === 0 || !canSaveSummaries}
              >
                Save summaries
              </Button>
            </Stack>
          </Stack>
        </Box>
      );
    }

    if (activeStep === 2) {
      return (
        <Box>
          <Stack spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: brand }}>
              Migrated Loans (Running loans)
            </Typography>
            <Alert severity="info">
              Migrated loans <b>do not create a new disbursement</b> and the system does not back-calculate interest.
              A repayment schedule is generated <b>from the migration date forward</b>.
            </Alert>

            <Paper sx={{ p: 2, borderRadius: 3, background: "#f5f5f5" }}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Add migrated loan
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      label="Member"
                      value={loanDraft.member}
                      onChange={(e) => setLoanDraft((p) => ({ ...p, member: e.target.value }))}
                      fullWidth
                      disabled={busy || isLocked}
                    >
                      {members.map((m) => (
                        <MenuItem key={m.id} value={m.id}>
                          {m.full_name} ({m.phone_number})
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Outstanding principal"
                      value={loanDraft.outstanding_principal}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/,/g, "");
                        if (!/^\d*(\.\d*)?$/.test(raw)) return;
                        setLoanDraft((p) => ({
                          ...p,
                          outstanding_principal: raw ? Number(raw).toLocaleString("en-US") : "",
                        }));
                      }}
                      fullWidth
                      disabled={busy || isLocked}
                      error={loanDraft.outstanding_principal !== "" && !isNonNegative(loanDraft.outstanding_principal)}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Outstanding interest"
                      value={loanDraft.outstanding_interest}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/,/g, "");
                        if (!/^\d*(\.\d*)?$/.test(raw)) return;
                        setLoanDraft((p) => ({
                          ...p,
                          outstanding_interest: raw ? Number(raw).toLocaleString("en-US") : "",
                        }));
                      }}
                      fullWidth
                      disabled={busy || isLocked}
                      error={loanDraft.outstanding_interest !== "" && !isNonNegative(loanDraft.outstanding_interest)}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Outstanding charge (optional)"
                      value={loanDraft.outstanding_df}
                      // Read-only: managed by auto-calculation
                      InputProps={{ readOnly: true }}
                      fullWidth
                      disabled={busy || isLocked}
                      error={loanDraft.outstanding_df !== "" && !isNonNegative(loanDraft.outstanding_df)}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Migrated arrears (optional)"
                      value={loanDraft.outstanding_arrears}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/,/g, "");
                        if (!/^\d*(\.\d*)?$/.test(raw)) return;
                        setLoanDraft((p) => ({
                          ...p,
                          outstanding_arrears: raw ? Number(raw).toLocaleString("en-US") : "",
                        }));
                      }}
                      fullWidth
                      disabled={busy || isLocked}
                      error={loanDraft.outstanding_arrears !== "" && !isNonNegative(loanDraft.outstanding_arrears)}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      label="Remaining term"
                      value={loanDraft.term_months}
                      onChange={(e) => setLoanDraft((p) => ({ ...p, term_months: e.target.value }))}
                      fullWidth
                      disabled={busy || isLocked}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      select
                      label="Term type"
                      value={loanDraft.term_type}
                      onChange={(e) => setLoanDraft((p) => ({ ...p, term_type: e.target.value }))}
                      fullWidth
                      disabled={busy || isLocked}
                    >
                      <MenuItem value="DAILY">Daily</MenuItem>
                      <MenuItem value="WEEKLY">Weekly</MenuItem>
                      <MenuItem value="MONTHLY">Monthly</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Interest rate % (optional)"
                      value={loanDraft.interest_rate}
                      onChange={(e) => setLoanDraft((p) => ({ ...p, interest_rate: e.target.value }))}
                      fullWidth
                      disabled={busy || isLocked}
                    />
                  </Grid>

                </Grid>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="flex-end">
                  <Button variant="contained" onClick={handleAddMigratedLoan} disabled={!canSaveLoanDraft || busy || isLocked}>
                    Save migrated loan
                  </Button>
                </Stack>
              </Stack>
            </Paper>

            <Typography sx={{ fontWeight: 800 }}>Migrated loans ({migratedLoans.length})</Typography>
            {migratedLoans.length === 0 ? (
              <Alert severity="warning">
                If the group has outstanding loans, add them here so schedules start after migration.
              </Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Member</TableCell>
                    <TableCell align="right">Outstanding total</TableCell>
                    <TableCell align="right">Term</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {migratedLoans.map((l, idx) => {
                    const member = members.find((m) => Number(m.id) === Number(l.member));
                    const total =
                      parseAmount(l.outstanding_principal) +
                      parseAmount(l.outstanding_interest) +
                      parseAmount(l.outstanding_df) +
                      parseAmount(l.outstanding_arrears || 0);
                    return (
                      <TableRow key={`${l.member}-${idx}`}>
                        <TableCell>{member?.full_name || l.member}</TableCell>
                        <TableCell align="right">{formatMoney(total)} TZS</TableCell>
                        <TableCell align="right">
                          {l.term_months} {String(l.term_type || "").toLowerCase()}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" color="error" disabled={isLocked} onClick={() => handleRemoveLoan(idx)}>
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Stack>
        </Box>
      );
    }

    if (activeStep === 5) {
      return (
        <Box>
          <Stack spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: brand }}>
              Group Business Opening Balances (Optional)
            </Typography>
            <Alert severity="info">Best practice: migrate business balances only. Transactions start fresh after migration.</Alert>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Opening cash balance"
                  value={businessBalance.opening_cash_balance}
                  onChange={(e) => setBusinessBalance((p) => ({ ...p, opening_cash_balance: e.target.value }))}
                  fullWidth
                  disabled={busy || isLocked}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Assets value"
                  value={businessBalance.opening_assets_value}
                  onChange={(e) => setBusinessBalance((p) => ({ ...p, opening_assets_value: e.target.value }))}
                  fullWidth
                  disabled={busy || isLocked}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Notes (optional)"
                  value={businessBalance.notes}
                  onChange={(e) => setBusinessBalance((p) => ({ ...p, notes: e.target.value }))}
                  fullWidth
                  disabled={busy || isLocked}
                />
              </Grid>
            </Grid>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="flex-end">
              <Button variant="contained" onClick={handleSaveBusiness} disabled={busy || isLocked || !canSaveBusinessBalance}>
                Save business balances
              </Button>
            </Stack>
          </Stack>
        </Box>
      );
    }

    if (activeStep === 6) {
      return (
        <Box>
          <Stack spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: brand }}>
              Review & Finalize
            </Typography>
            <Alert severity="warning">
              Finalization locks migration records and activates the group. Penalties & automated schedules start after finalization.
            </Alert>

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    Snapshot
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Group: {groupNameDisplay}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Migration date: {migration?.migration_date || groupDraft.migration_date}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Members: {members.length}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    Totals
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Savings: {formatMoney(totals.totalSavings)} TZS
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Special: {formatMoney(totals.totalSpecial)} TZS
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Emergency: {formatMoney(totals.totalEmergency)} TZS
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Arrears: {formatMoney(totals.totalArrears)} TZS
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    Loans
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Migrated loans: {migratedLoans.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Outstanding total: {formatMoney(totals.totalLoanOutstanding)} TZS
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {(missingBalances > 0 || missingSummaries > 0) && (
              <Alert severity="error">
                Missing per-member records: opening balances missing for {missingBalances}, summaries missing for {missingSummaries}.
                Complete these before submitting for review or approving.
              </Alert>
            )}
            {missingLeadershipLabels.length > 0 && (
              <Alert severity="error">
                Missing leadership roles: {missingLeadershipLabels.join(", ")}. Add these members before review/approval.
              </Alert>
            )}

            <Divider />

            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1}
              justifyContent="space-between"
              alignItems={{ md: "center" }}
            >
              <Box>
                <Typography sx={{ fontWeight: 800 }}>Workflow</Typography>
                <Typography variant="body2" color="text.secondary">
                  Draft → In Review → Approved → Finalized · Request changes/Reopen returns to Draft
                </Typography>
              </Box>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                {status === "DRAFT" && (
                  <Button variant="outlined" onClick={handleSubmitForReview} disabled={busy || !canSubmitForReview}>
                    Submit for review
                  </Button>
                )}
                {status === "IN_REVIEW" && (
                  <>
                    <Button
                      variant="outlined"
                      color="warning"
                      onClick={() =>
                        setTransitionDialog({
                          open: true,
                          title: "Request changes",
                          nextStatus: "DRAFT",
                          reason: "",
                        })
                      }
                      disabled={busy}
                    >
                      Request changes
                    </Button>
                    <Button variant="outlined" onClick={handleApprove} disabled={busy || !canApprove}>
                      Approve
                    </Button>
                  </>
                )}
                {status === "APPROVED" && (
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={() =>
                      setTransitionDialog({
                        open: true,
                        title: "Reopen migration",
                        nextStatus: "DRAFT",
                        reason: "",
                      })
                    }
                    disabled={busy}
                  >
                    Reopen
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<LockIcon />}
                  onClick={() => setFinalizeDialog({ open: true, typed: "" })}
                  disabled={busy || !canFinalize}
                >
                  Finalize
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Box>
      );
    }

    return null;
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 900, color: brand, mb: 0.5 }}>
          Group Migration
        </Typography>
        <Typography variant="subtitle2" color="text.secondary">
          Admin-only onboarding for paper-based groups.
        </Typography>
      </Box>

      <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 2 }}>
        <CardContent>
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }} justifyContent="space-between">
              <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                <Chip label={`Mode: ${useMock ? "Mock" : "Live API"}`} variant="outlined" />
                <Chip label={`Status: ${statusLabel}`} variant="outlined" sx={getStatusChipStyles(status, true)} />
                {!!authUser?.full_name && <Chip label={`User: ${authUser.full_name}`} variant="outlined" />}
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => setConfirm({ open: true, action: "RESET" })}
                >
                  Reset
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<SyncAltIcon />}
                  onClick={() => setConfirm({ open: true, action: "SWITCH" })}
                  disabled={!domain}
                >
                  Switch to {useMock ? "Live API" : "Mock"}
                </Button>
              </Stack>
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap">
              {STATUS_ORDER.map((statusKey, index) => (
                <StatusPill
                  key={statusKey}
                  status={statusKey}
                  label={STATUS_LABEL[statusKey]}
                  active={index <= statusIndex}
                />
              ))}
            </Stack>

            <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 8 }} />
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
        <CardContent>
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between" alignItems={{ md: "center" }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: brand }}>
                  {stepTitle}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {migration ? `Migration #${migration.id} · ${groupNameDisplay}` : "Start by creating a migration draft."}
                </Typography>
              </Box>
              {busy && <Chip label="Working…" color="warning" variant="outlined" />}
            </Stack>

            <Stepper activeStep={activeStep} alternativeLabel>
              {WIZARD_STEPS.map((s) => (
                <Step key={s.key}>
                  <StepLabel>{s.label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {renderStep()}

            <Divider />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between">
              <Button variant="outlined" onClick={() => setActiveStep((s) => Math.max(0, s - 1))} disabled={busy || activeStep === 0}>
                Back
              </Button>
              <Button
                variant="contained"
                onClick={() => setActiveStep((s) => Math.min(WIZARD_STEPS.length - 1, s + 1))}
                disabled={busy || activeStep === WIZARD_STEPS.length - 1 || !canGoNext}
              >
                Next
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={confirm.open} onClose={() => setConfirm({ open: false, action: null })} maxWidth="sm" fullWidth>
        <DialogTitle>{confirm.action === "SWITCH" ? "Switch mode" : "Reset wizard"}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will clear the current wizard state. If you haven’t saved to the server, changes will be lost.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm({ open: false, action: null })}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              const action = confirm.action;
              setConfirm({ open: false, action: null });
              if (action === "SWITCH") {
                resetAll();
                setUseMock((prev) => !prev);
                showInfo("Mode switched.");
              } else {
                resetAll();
                showInfo("Reset complete.");
              }
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={transitionDialog.open}
        onClose={() => setTransitionDialog({ open: false, title: "", nextStatus: "", reason: "" })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{transitionDialog.title || "Return to Draft"}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will return the migration to Draft and unlock edits. Previous approvals will be cleared.
          </Alert>
          <TextField
            label="Reason"
            value={transitionDialog.reason}
            onChange={(e) =>
              setTransitionDialog((prev) => ({ ...prev, reason: e.target.value }))
            }
            fullWidth
            required
            multiline
            minRows={2}
            helperText="Required for audit trail."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransitionDialog({ open: false, title: "", nextStatus: "", reason: "" })}>
            Cancel
          </Button>
          <Button color="warning" variant="contained" onClick={handleConfirmTransition}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={finalizeDialog.open} onClose={() => setFinalizeDialog({ open: false, typed: "" })} maxWidth="sm" fullWidth>
        <DialogTitle>Finalize migration</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            Finalization is irreversible: migration records lock, group becomes ACTIVE, penalties & schedules start after the finalize timestamp.
          </Alert>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Type the group name to confirm:
          </Typography>
          <TextField
            label="Group name confirmation"
            value={finalizeDialog.typed}
            onChange={(e) => setFinalizeDialog((p) => ({ ...p, typed: e.target.value }))}
            fullWidth
          />
          <Typography variant="caption" color="text.secondary">
            Expected: {(migration?.group_name || groupDraft.name || "").trim()}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFinalizeDialog({ open: false, typed: "" })}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleFinalize} disabled={busy}>
            Finalize
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
